import { ReactNode, createContext, useContext, useState } from "react";
import { RefreshToken } from "../services/auth";
import { toast } from "react-toastify";
import { Navigate, useNavigate } from "react-router-dom";
import { sendWebViewMessage, MessageTypes } from "../utils/webViewBridge";
import { currentFeatures } from "@/configs/Settings";
import {
  clearAllTokens,
  getUserAccessTokenExpiresAt,
  setRefreshToken,
  setUserAccessToken,
  setUserAccessTokenExpiresAt,
} from "src-next/shared/api/utils/tokenStorage";

interface AuthContextValue {
  parsedTime: any;
  timeLeft: any;
  getNewToken: any;
  logoutUser: any;
}

export const AuthContext = createContext<AuthContextValue>(null!);

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(
    localStorage.getItem("logged") ? true : false
  );

  // Expiração do access token (ms absolutos) centralizada em tokenStorage (sessionStorage).
  const [expirationTime, setExpirationTime] = useState<string>(() => {
    const at = getUserAccessTokenExpiresAt();
    return at != null ? String(at) : "";
  });
  const parsedTime = expirationTime ? parseInt(expirationTime, 10) : 0;

  const timeLeft = parsedTime ? parsedTime - Date.now() : 0;

  function logoutUser() {
    setAuth(false);
    // Remove tokens namespaced (sessionStorage); demais chaves seguem no localStorage.clear abaixo.
    clearAllTokens();
    localStorage.clear();
    toast.error("Sua sessão expirou realize o signin novamente");
    localStorage.clear();
    // Notifica o app nativo sobre o logout
    sendWebViewMessage({
      type: MessageTypes.LOGOUT,
    });
    navigate("/signin");
  }

  async function getNewToken() {
    if (auth) {
      try {
        const { access_token, refresh_token, expires_in } =
          await RefreshToken();
        const nextExpirationMs = Date.now() + expires_in * 1000;
        setExpirationTime(String(nextExpirationMs));
        setUserAccessToken(access_token);
        setRefreshToken(refresh_token);
        setUserAccessTokenExpiresAt(nextExpirationMs);
      } catch (error) {
        logoutUser();
      }
    }
  }

  if (!auth)
    return <Navigate to={currentFeatures.initialRoute || "/signin"} replace />;

  const contextValue = {
    parsedTime,
    timeLeft,
    getNewToken,
    logoutUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}
